import {
  AndonBoardLayoutItem,
  AndonBoardTextLayoutItem,
  AndonGridCell,
  BoardLayout,
  BoardTextLabelFields,
  BorderSide,
  WorkingLayout
} from "~/features/andon/andon.types";
import {
  calculateAndonGridCellKey,
  calculateAndonPortKey,
  buildPerimeterCellBorders,
  computeFontSize,
  calculateMinAndonBoardDimensions,
  isTextLayoutItem
} from "~/features/andon/andon.utils";
import {
  findClosestEmptyPositions,
  getEmptyPositions,
  convertPixelCoordinatesToGridCoordinates
} from "~/lib/helpers";
import { WorkstationSummaryDto } from "~/types/api";

export function upsertSavedBoardLayout(
  layouts: BoardLayout[],
  newLayout: BoardLayout,
  options: { isRename: boolean; originalName?: string | null }
): BoardLayout[] {
  let updatedLayouts = [...layouts];

  if (options.isRename && options.originalName) {
    updatedLayouts = updatedLayouts.filter(
      (layout) => layout.name !== options.originalName
    );
  }

  const existingIndex = updatedLayouts.findIndex(
    (layout) => layout.name === newLayout.name
  );

  if (existingIndex >= 0) {
    updatedLayouts[existingIndex] = newLayout;
    return updatedLayouts;
  }

  return [...updatedLayouts, newLayout];
}

export function applyBoardDimensionChange(
  workingLayout: WorkingLayout,
  columns: number,
  rows: number
): WorkingLayout {
  const { columns: minColumns, rows: minRows } =
    calculateMinAndonBoardDimensions(workingLayout.layout);
  const nextColumns = Math.max(columns, minColumns);
  const nextRows = Math.max(rows, minRows);

  return {
    ...workingLayout,
    dimensions: { columns: nextColumns, rows: nextRows },
    layout: workingLayout.layout.map((boardLayoutItem) => ({
      ...boardLayoutItem,
      maxH: nextRows - boardLayoutItem.y
    }))
  };
}

export function replaceWorkstationOnLayout(
  layoutItems: AndonBoardLayoutItem[],
  selectedGridIndex: string,
  workstationId: string,
  fulfillmentCenterId?: string
): AndonBoardLayoutItem[] {
  return layoutItems.map((boardLayoutItem) => {
    if (boardLayoutItem.i !== selectedGridIndex) {
      return boardLayoutItem;
    }
    return {
      kind: "workstation" as const,
      i: workstationId,
      fulfillmentCenterId,
      x: boardLayoutItem.x,
      y: boardLayoutItem.y,
      w: boardLayoutItem.w,
      h: boardLayoutItem.h,
      maxH: boardLayoutItem.maxH
    };
  });
}

export function findClosestEmptyBoardPosition({
  layoutItems,
  columns,
  rows,
  boardRect,
  anchor,
  boardWidth,
  boardHeight,
  gridHeightPercentage
}: {
  layoutItems: AndonBoardLayoutItem[];
  columns: number;
  rows: number;
  boardRect: DOMRect;
  anchor: { left: number; top: number };
  boardWidth: number;
  boardHeight: number;
  gridHeightPercentage: number;
}): { x: number; y: number } | null {
  const clickedGridPosition = convertPixelCoordinatesToGridCoordinates(
    anchor.left - boardRect.left,
    anchor.top - boardRect.top,
    boardWidth,
    boardHeight * (gridHeightPercentage / 100),
    columns
  );

  const emptyCoordinates = getEmptyPositions(layoutItems, columns, rows);
  return findClosestEmptyPositions(clickedGridPosition, emptyCoordinates);
}

export function createWorkstationLayoutItem({
  workstationId,
  fulfillmentCenterId,
  position,
  rows
}: {
  workstationId: string;
  fulfillmentCenterId?: string;
  position: { x: number; y: number };
  rows: number;
}): AndonBoardLayoutItem {
  return {
    kind: "workstation",
    i: workstationId,
    fulfillmentCenterId,
    x: position.x,
    y: position.y,
    w: 1,
    h: 1,
    maxH: rows - position.y
  };
}

export function createTextLayoutItem({
  textLabel,
  position,
  rows
}: {
  textLabel: BoardTextLabelFields;
  position: { x: number; y: number };
  rows: number;
}): AndonBoardTextLayoutItem {
  return {
    kind: "text",
    i: textLabel.id,
    text: textLabel.text,
    backgroundColor: textLabel.backgroundColor,
    textColor: textLabel.textColor,
    fontSize: textLabel.fontSize,
    x: position.x,
    y: position.y,
    w: 1,
    h: 1,
    maxH: rows - position.y
  };
}

export function updateTextLayoutItem(
  layoutItems: AndonBoardLayoutItem[],
  textLabel: BoardTextLabelFields
): AndonBoardLayoutItem[] {
  return layoutItems.map((boardLayoutItem) =>
    boardLayoutItem.i === textLabel.id && isTextLayoutItem(boardLayoutItem)
      ? {
          ...boardLayoutItem,
          text: textLabel.text,
          backgroundColor: textLabel.backgroundColor,
          textColor: textLabel.textColor,
          fontSize: textLabel.fontSize
        }
      : boardLayoutItem
  );
}

export function applyCellBordersToWorkingLayout(
  workingLayout: WorkingLayout,
  targetCells: AndonGridCell[],
  sides: BorderSide[],
  color: string
): WorkingLayout {
  if (targetCells.length === 0) {
    return workingLayout;
  }
  const targetKeys = new Set(targetCells.map(calculateAndonGridCellKey));
  const remainingBorders = workingLayout.cellBorders.filter(
    (cellBorder) => !targetKeys.has(calculateAndonGridCellKey(cellBorder))
  );
  if (sides.length === 0) {
    return {
      ...workingLayout,
      cellBorders: remainingBorders
    };
  }
  return {
    ...workingLayout,
    cellBorders: [
      ...remainingBorders,
      ...buildPerimeterCellBorders(targetCells, sides, color)
    ]
  };
}

export function buildBoardPortToWorkstationId(
  workstations: WorkstationSummaryDto[]
): Record<string, string> {
  const portToWorkstationId: Record<string, string> = {};
  for (const workstation of workstations) {
    const gridId = workstation.autostoreGridId;
    for (const port of workstation.ports ?? []) {
      portToWorkstationId[calculateAndonPortKey(gridId, port.portId)] =
        workstation.id;
      if (port.parentPortId != null) {
        portToWorkstationId[calculateAndonPortKey(gridId, port.parentPortId)] =
          workstation.id;
      }
    }
  }
  return portToWorkstationId;
}

export function computeAndonTileFontSize({
  boardHeight,
  heightSpan,
  rows,
  boardScale
}: {
  boardHeight: number;
  heightSpan: number;
  rows: number;
  boardScale: number;
}): string {
  const heightPx = boardHeight * (heightSpan / rows);
  const baseFontSize = computeFontSize({
    heightPx,
    fontSizeMultiplier: heightSpan <= 1 ? 0.2 : 0.4,
    upperFontSizeLimit: 25,
    lowerFontSizeLimit: 9
  });
  return `${parseFloat(baseFontSize) * boardScale}px`;
}
